import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Tab,
  Tabs,
  Stack,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  CircularProgress,
  Tooltip,
  alpha,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useElementSize } from '@custom-react-hooks/use-element-size';
import { useConfirm } from 'material-ui-confirm';
import { useTranslation } from 'react-i18next';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PsychologyIcon from '@mui/icons-material/Psychology';
import BuildIcon from '@mui/icons-material/Build';
import TokenIcon from '@mui/icons-material/Token';
import CloseIcon from '@mui/icons-material/Close';

import PipelineBuilder from '@/components/pipelineBuilder/pipelineBuilder';
import PipelineConfiguration from './configuration';
import PipelineAdministration from './administration';
import PipelineHeader from './header';
import { useApiContext } from '@/contexts/apiContext';
import { useUserContext } from '@/contexts/userContext';
import { useNotificationContext } from '@/contexts/notificationContext';
import ProgressIndicator from '@/components/progressIndicator';
import TabPanel from '@/components/tabPanel';
import ErrorMessage from '@/components/errorMessage';
import { EntityType } from '@/components/entities/entityType';

const PipelineInstance = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const apiContext = useApiContext();
  const { hasPermission } = useUserContext();
  const { toast } = useNotificationContext();
  const [searchParams] = useSearchParams();

  const pipelineInstanceIdParam = searchParams.get('pipelineInstanceId');

  const [containerRef, containerSize] = useElementSize();
  const [topPanelRef, topPanelSize] = useElementSize();

  const [pipeline, setPipeline] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [selectedTab, setSelectedTab] = useState<string>('builder');
  const [optionTabs, setOptionTabs] = useState<any[]>([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const menuOpen = Boolean(menuAnchorEl);

  // Surface B: Pending approvals & ReAct metrics states
  const [pendingApprovals, setPendingApprovals] = useState<any[]>([]);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewFeedback, setReviewFeedback] = useState('');
  const [reactMetrics, setReactMetrics] = useState<{
    iterations: number;
    toolCalls: number;
    tokens: number;
    autonomousAgentsCount: number;
  }>({
    iterations: 0,
    toolCalls: 0,
    tokens: 0,
    autonomousAgentsCount: 0,
  });

  const fetchPipeline = async () => {
    if (!pipelineInstanceIdParam) {
      setPipeline(null);
      return;
    }
    try {
      setLoading(true);
      const data = await apiContext.getPipelineInstance(pipelineInstanceIdParam);
      setPipeline(data);

      // Fetch pending approvals for pipeline
      try {
        const approvals = await apiContext.getPendingApprovals(pipelineInstanceIdParam);
        setPendingApprovals(Array.isArray(approvals) ? approvals : []);
      } catch (e) {
        console.warn('Failed to load pending approvals:', e);
      }

      // Compute reasoning & execution metrics
      if (data?.items) {
        let totalTokens = 0;
        let actualIterations = 0;
        let actualToolCalls = 0;
        let autoAgents = 0;
        const agents = data.items.filter((item: any) => item.entityType === EntityType.agentInstance);
        autoAgents = agents.length;

        for (const agent of agents) {
          try {
            const usages = await apiContext.getEntityLlmUsages(agent.entityType, agent.agentDefId, agent.id);
            if (Array.isArray(usages)) {
              totalTokens += usages.reduce((sum: number, u: any) => sum + (Number(u.totalTokens) || 0), 0);
            }
          } catch (e) {
            // ignore
          }

          try {
            const statuses = await apiContext.getAgentInstanceItemProcessingStatuses(
              agent.agentDefId,
              agent.id,
              pipelineInstanceIdParam,
            );
            if (Array.isArray(statuses)) {
              for (const s of statuses) {
                const trajectory =
                  s.output?.trajectory ||
                  s.checkpointState?.trajectory ||
                  (s.output && Array.isArray(s.output) ? s.output : null);
                if (Array.isArray(trajectory)) {
                  actualIterations += trajectory.length;
                  actualToolCalls += trajectory.filter(
                    (step: any) => step.type === 'tool_call' || step.toolName || step.action,
                  ).length;
                }
              }
            }
          } catch (e) {
            // ignore
          }
        }

        setReactMetrics({
          iterations: actualIterations,
          toolCalls: actualToolCalls,
          tokens: totalTokens,
          autonomousAgentsCount: autoAgents,
        });
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResolveApproval = async (approval: any, approved: boolean) => {
    if (!pipelineInstanceIdParam) return;
    setReviewSubmitting(true);
    try {
      const stepId = approval.stepId || approval.entityId || approval.id;
      await apiContext.resumePipelineStep(pipelineInstanceIdParam, stepId, {
        approved,
        feedback: reviewFeedback,
        entityDefId: approval.entityDefId,
        dataSourceItemId: approval.dataSourceItemId,
        itemProcessingStatusId: approval.itemProcessingStatusId,
      });
      toast({ message: approved ? 'Step approved and resumed' : 'Step rejected' });
      // Refresh pending approvals
      const updated = await apiContext.getPendingApprovals(pipelineInstanceIdParam);
      setPendingApprovals(Array.isArray(updated) ? updated : []);
      if (!updated || updated.length === 0) {
        setReviewModalOpen(false);
      }
    } catch (err: any) {
      toast({ message: err?.message || 'Failed to process approval', type: 'error' });
    } finally {
      setReviewSubmitting(false);
    }
  };

  useEffect(() => {
    fetchPipeline();
  }, [pipelineInstanceIdParam]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const openOptionsMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
  };

  const addOptionsTab = (option: any) => {
    const existing = optionTabs.find((o) => o.value === option.value);
    if (existing) {
      setSelectedTab(existing.value);
    } else {
      setOptionTabs([...optionTabs, option]);
      setSelectedTab(option.value);
    }
    closeMenu();
  };

  const deleteOptionTab = (value: string) => {
    setOptionTabs(optionTabs.filter((o) => o.value !== value));
    if (selectedTab === value) {
      setSelectedTab('builder');
    }
  };

  const handleUpdatePipelineConfiguration = async (updated: { name: string; description: string }) => {
    if (!pipeline?.id) return;
    try {
      await apiContext.submitPipeline({
        pipelineInstanceId: pipeline.id,
        name: updated.name,
        description: updated.description,
        blueprintId: pipeline.blueprintId,
        entities: (pipeline.items || []).map((item: any) => ({
          id: item.id,
          name: item.name,
          description: item.description,
          entityType: item.entityType,
          entityDefId: item.entityDefId,
          settings: item.settings,
          eventSubscriptions: item.eventSubscriptions,
          sourceEntities: item.sourceEntities,
        })),
      });
      toast({ message: 'Pipeline configuration saved successfully' });
      await fetchPipeline();
    } catch (err: any) {
      toast({ message: err?.message || 'Failed to update pipeline configuration', type: 'error' });
      throw err;
    }
  };

  const handleDeletePipeline = () => {
    confirm({
      description: 'Are you sure you want to delete this pipeline instance? This will remove all connections.',
      confirmationText: 'Delete',
      cancellationText: 'Cancel',
      title: 'Delete Pipeline',
    }).then(async () => {
      try {
        await apiContext.deletePipelineInstance(pipeline.id, true);
        toast({ message: 'Pipeline deleted successfully' });
        navigate('/dashboard');
      } catch (err: any) {
        toast({ message: err?.message || 'Failed to delete pipeline', type: 'error' });
      }
    });
  };

  const close = () => {
    navigate('/dashboard');
  };

  const tabs = useMemo(() => {
    const list: any[] = [
      {
        value: 'builder',
        label: 'Pipeline Builder',
        icon: <AccountTreeIcon sx={{ fontSize: 18 }} />,
      },
    ];

    optionTabs.forEach((ot) => {
      list.push({ ...ot, isOption: true });
    });

    return list;
  }, [optionTabs]);

  if (loading && !pipeline) {
    return <ProgressIndicator />;
  }

  const containerHeight = containerSize.height || 500;
  const topPanelHeight = topPanelSize.height || 100;
  const contentHeight = containerHeight - topPanelHeight;

  return (
    <Stack
      ref={containerRef}
      sx={{
        height: '100%',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Top Panel: Header + Tabs */}
      <Box ref={topPanelRef} sx={{ display: 'flex', flexDirection: 'column', zIndex: 10 }}>
        <PipelineHeader pipeline={pipeline} onClose={close} />
        {error && <ErrorMessage error={error} />}

        {/* Pinned Top Notification Bar: Pending Approvals Across Entire Pipeline */}
        {pendingApprovals.length > 0 && (
          <Box
            sx={{
              mx: 1,
              mt: 0.5,
              mb: 0.25,
              px: 1.5,
              py: 0.75,
              borderRadius: '10px',
              bgcolor: '#fffbeb',
              border: '1px solid #fde68a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 2px 8px rgba(245, 158, 11, 0.1)',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <WarningAmberIcon
                sx={{
                  color: '#d97706',
                  fontSize: 18,
                  animation: 'pulse 1.5s infinite',
                  '@keyframes pulse': {
                    '0%': { transform: 'scale(0.95)', opacity: 0.8 },
                    '50%': { transform: 'scale(1.15)', opacity: 1 },
                    '100%': { transform: 'scale(0.95)', opacity: 0.8 },
                  },
                }}
              />
              <Typography variant="caption" sx={{ color: '#92400e', fontWeight: 700, fontSize: '0.78rem' }}>
                Pipeline paused: {pendingApprovals.length} human approval{pendingApprovals.length > 1 ? 's' : ''} required before downstream execution can proceed.
              </Typography>
            </Stack>
            <Button
              size="small"
              variant="contained"
              onClick={() => setReviewModalOpen(true)}
              sx={{
                bgcolor: '#f59e0b',
                color: '#ffffff',
                textTransform: 'none',
                fontWeight: 800,
                fontSize: '0.72rem',
                py: 0.25,
                px: 1.5,
                borderRadius: '6px',
                '&:hover': { bgcolor: '#d97706' },
              }}
            >
              Review Now
            </Button>
          </Box>
        )}

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="stretch"
          className="glass-card-light"
          sx={{
            mx: 1,
            my: 0.5,
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: 'none',
            overflow: 'hidden',
            pb: { xs: 1, sm: 0 },
          }}
        >
          <Box sx={{ maxWidth: { xs: '100%', sm: '55vw' }, width: '100%', pl: 1 }}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              indicatorColor="secondary"
              variant="scrollable"
              sx={{
                minHeight: '40px',
                '& .MuiTabs-indicator': {
                  height: '3px',
                  borderRadius: '3px 3px 0 0',
                  bgcolor: '#8BD8BC',
                },
              }}
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  icon={tab.icon}
                  iconPosition="start"
                  sx={{
                    minHeight: '40px',
                    px: tab.isOption ? 2 : 3,
                    transition: 'all 0.2s',
                    position: 'relative',
                    overflow: 'visible',
                    opacity: selectedTab === tab.value ? 1 : 0.7,
                    '&:hover': { opacity: 1, bgcolor: 'rgba(139, 216, 188, 0.05)' },
                  }}
                  label={
                    <Box component="span" sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', pr: tab.isOption ? 1.5 : 0 }}>
                      <Typography
                        variant={selectedTab === tab.value ? 'subtitle2' : 'caption'}
                        sx={{
                          fontWeight: selectedTab === tab.value ? '800' : '500',
                          color: selectedTab === tab.value ? 'primary.main' : 'text.secondary',
                        }}
                      >
                        {t(tab.label)}
                      </Typography>
                      {tab.isOption && (
                        <IconButton
                          size="small"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            deleteOptionTab(tab.value);
                          }}
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: -6,
                            p: 0.1,
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 10 }} />
                        </IconButton>
                      )}
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>

          {/* Autonomous ReAct & Swarm Metrics Cards + Options Button */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ px: 1.5, py: 0.5, flexWrap: 'wrap', gap: 0.5 }}
          >
            {reactMetrics.iterations > 0 && (
              <Tooltip title="Total ReAct iterations completed across autonomous agents">
                <Chip
                  size="small"
                  icon={<PsychologyIcon sx={{ fontSize: '12px !important', color: '#7c3aed !important' }} />}
                  label={`${reactMetrics.iterations} Iterations`}
                  sx={{
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 750,
                    bgcolor: '#f5f3ff',
                    color: '#6d28d9',
                    border: '1px solid #ddd6fe',
                    '& .MuiChip-label': { px: 0.5 },
                  }}
                />
              </Tooltip>
            )}

            {reactMetrics.toolCalls > 0 && (
              <Tooltip title="MCP Tools invoked during autonomous pipeline runs">
                <Chip
                  size="small"
                  icon={<BuildIcon sx={{ fontSize: '11px !important', color: '#0284c7 !important' }} />}
                  label={`${reactMetrics.toolCalls} Tool Calls`}
                  sx={{
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 750,
                    bgcolor: '#f0f9ff',
                    color: '#0369a1',
                    border: '1px solid #bae6fd',
                    '& .MuiChip-label': { px: 0.5 },
                  }}
                />
              </Tooltip>
            )}

            {reactMetrics.tokens > 0 && (
              <Tooltip title="Total LLM tokens consumed across agents and apps">
                <Chip
                  size="small"
                  icon={<TokenIcon sx={{ fontSize: '12px !important', color: '#059669 !important' }} />}
                  label={`${reactMetrics.tokens > 1000000 ? `${(reactMetrics.tokens / 1000000).toFixed(1)}M` : reactMetrics.tokens > 1000 ? `${(reactMetrics.tokens / 1000).toFixed(1)}k` : reactMetrics.tokens} Tokens`}
                  sx={{
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 750,
                    bgcolor: '#ecfdf5',
                    color: '#047857',
                    border: '1px solid #a7f3d0',
                    '& .MuiChip-label': { px: 0.5 },
                  }}
                />
              </Tooltip>
            )}

            {/* Options Button */}
            <Button
              size="small"
              color="primary"
              variant="contained"
              onClick={openOptionsMenu}
              endIcon={<KeyboardArrowDownIcon />}
              sx={{
                borderRadius: '20px',
                px: 2,
                bgcolor: 'primary.main',
                fontWeight: 'bold',
                boxShadow: '0 4px 12px rgba(35, 53, 101, 0.2)',
                '&:hover': { bgcolor: 'primary.dark', boxShadow: '0 6px 16px rgba(35, 53, 101, 0.3)' },
                textTransform: 'none',
              }}
            >
              {t('common.buttons.options', 'Options')}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden', position: 'relative', mt: 1 }}>
        {containerHeight > 0 && topPanelHeight > 0 && (
          <>
            <TabPanel value="builder" currentTab={selectedTab} maxHeight={contentHeight}>
              <PipelineBuilder
                maxHeight={contentHeight}
                entity={undefined}
                onConfigurationChange={fetchPipeline}
                isOpen={selectedTab === 'builder'}
              />
            </TabPanel>

            {pipeline && (
              <TabPanel value="configuration" currentTab={selectedTab} maxHeight={contentHeight}>
                <PipelineConfiguration
                  pipeline={pipeline}
                  maxHeight={contentHeight}
                  onSave={handleUpdatePipelineConfiguration}
                  onCancel={() => deleteOptionTab('configuration')}
                />
              </TabPanel>
            )}

            {pipeline && (
              <TabPanel value="administration" currentTab={selectedTab} maxHeight={contentHeight}>
                <PipelineAdministration
                  pipeline={pipeline}
                  isOpen={selectedTab === 'administration'}
                  maxHeight={contentHeight}
                  onDelete={handleDeletePipeline}
                />
              </TabPanel>
            )}
          </>
        )}
      </Box>

      {/* Review Approvals Dialog */}
      <Dialog
        open={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px', p: 1 },
        }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <WarningAmberIcon sx={{ color: '#f59e0b' }} />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Pipeline Pending Approvals
            </Typography>
          </Stack>
          <IconButton size="small" onClick={() => setReviewModalOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Review and resolve pending Human-in-the-Loop approval requests across this pipeline.
          </Typography>

          <Stack spacing={2}>
            {pendingApprovals.map((approval, aIdx) => {
              const actionTitle =
                approval.action ||
                approval.approvalRequest?.action ||
                approval.proposedAction?.action ||
                'High-Consequence Tool Action';
              const params =
                approval.parameters ||
                approval.approvalRequest?.parameters ||
                approval.proposedAction?.parameters ||
                approval.proposedAction?.args ||
                {};
              const rationale =
                approval.prompt ||
                approval.approvalRequest?.prompt ||
                approval.reason ||
                'Agent paused execution awaiting approval.';

              return (
                <Card
                  key={approval.id || aIdx}
                  variant="outlined"
                  sx={{ p: 2, borderRadius: '12px', border: '1px solid #fcd34d', bgcolor: '#fffdf5' }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 750, color: '#92400e' }}>
                      {actionTitle}
                    </Typography>
                    <Chip
                      size="small"
                      label={approval.stepName || approval.entityDefId || `Step #${approval.stepIndex ?? aIdx + 1}`}
                      sx={{ fontSize: '10px', height: 20, bgcolor: '#fef3c7', color: '#b45309' }}
                    />
                  </Stack>

                  <Typography variant="caption" sx={{ color: '#78350f', display: 'block', mb: 1 }}>
                    {rationale}
                  </Typography>

                  {Object.keys(params).length > 0 && (
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: '8px',
                        bgcolor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        overflowX: 'auto',
                        mb: 1.5,
                      }}
                    >
                      <pre style={{ margin: 0 }}>{JSON.stringify(params, null, 2)}</pre>
                    </Box>
                  )}

                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Optional feedback for agent (e.g. adjust dosage to 40mg)..."
                    value={reviewFeedback}
                    onChange={(e) => setReviewFeedback(e.target.value)}
                    sx={{ mb: 1.5, bgcolor: '#ffffff' }}
                  />

                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      disabled={reviewSubmitting}
                      onClick={() => handleResolveApproval(approval, false)}
                      sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                      Reject
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      disabled={reviewSubmitting}
                      onClick={() => handleResolveApproval(approval, true)}
                      sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                      {reviewSubmitting ? <CircularProgress size={16} color="inherit" /> : 'Approve & Resume'}
                    </Button>
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Options Menu */}
      <Menu
        id="optionsMenu"
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={closeMenu}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            mt: 1,
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            minWidth: 180,
          },
        }}
      >
        <MenuItem
          onClick={() =>
            addOptionsTab({
              value: 'configuration',
              label: 'Configuration',
              icon: <SettingsOutlinedIcon fontSize="small" />,
            })
          }
        >
          <ListItemIcon>
            <SettingsOutlinedIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}>
            {t('entity.instance.tabs.configuration', 'Configuration')}
          </ListItemText>
        </MenuItem>

        {(pipeline?.userAccess?.role === 'owner' ||
          hasPermission('pipeline:accessControl:view', pipeline?.userAccess) ||
          hasPermission('pipeline:usageStats:view', pipeline?.userAccess) ||
          hasPermission('pipeline:auditTrail:view', pipeline?.userAccess)) && (
          <MenuItem
            onClick={() =>
              addOptionsTab({
                value: 'administration',
                label: 'Administration',
                icon: <AdminPanelSettingsIcon fontSize="small" />,
              })
            }
          >
            <ListItemIcon>
              <AdminPanelSettingsIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}>
              {t('entity.instance.tabs.administration', 'Administration')}
            </ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Stack>
  );
};

export default PipelineInstance;
